import { Req, Res } from '@cbo/shared-library';
import { SortDirection } from '@mui/material/TableCell';
import dayjs from 'dayjs';
import axios, { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isBoolean } from 'lodash';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { checkFirebaseErrorCodeExists, HttpsError } from '../../contexts/firebaseApiContext';
import useCallBslAndHandleErrors from '../../lib/firebaseApiHandleErrors';
import { Paginated } from '../../models/Paginated';
import useCallBsl from '../../utils/hooks/useCallBsl';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import { getStoredLang } from '../../utils/langUtils/langUtils';
import toQueryParams from '../../utils/objectUtils/toQueryParams';
import { mockDefaultUsagePerFactor } from '../mocks/mockUsagePer';
import {
  InvoiceData,
  InvoiceDto,
  InvoiceStatus,
  InvoiceStatusAction,
  OcrMetaDataDto,
  UpdateOcrMetaDataDto,
} from '../models/Invoice';
import { CreateInvoiceItemDto, InvoiceItemData, UpdateInvoiceItemDto } from '../models/InvoiceItem';
import {
  CreateInventorySummaryDto,
  InventorySummaryList,
  InventorySummaryResponse,
  ListItem,
  ListsSummaryData,
  LocationDetailResponse,
  LocationDto,
  LocationSummary,
  LocationSummaryResponse,
  MobileInventoryStatus,
  UpdateCountSummary,
  UpdateListItemUnitDto,
  UpdateLocationItemUnitDto,
  UpdateLocationSummary,
} from '../models/Location';
import {
  CostCalcMethodApiResponse,
  InventoryUnitMappingDto,
  RawItemData,
  RawItemDto,
  UnitData,
  UpdateUsagePerData,
  ValidateRawItemCustomUnitData,
} from '../models/RawItem';
import {
  CreateRecipeDto,
  CreateRecipeResponse,
  getRecipesPaginatedParams,
  RecipeDto,
  RecipeRow,
  UpdateRecipeDto,
  UpdateRecipeResponse,
  RecipeCloneDto,
  getRecipesFilterPaginatedOptionsParams,
  RecipesFilterOptionsResponse,
} from '../models/Recipe';
import {
  getRecipeItemsPaginatedParams,
  RecipeItemData,
  RecipeItemRow,
  UpdateRecipeItemDto,
} from '../models/RecipeItem';
import {
  EntityMappingData,
  GetAllSalesItemsMappingsResponse,
  GetAllSalesItemsResponse,
  GetOneSalesItemsResponse,
  GetUnmappedSalesItemsResponse,
  MappingId,
  SalesItemMappingData,
} from '../models/SalesItem';
import {
  CombinedUnit,
  ContainerUnit,
  CustomUnit,
  CustomUnitDto,
  CustomUnitMappingDto,
  IndustryUnit,
  ReceivingUnit,
  VendorInventoryUnitsMap,
} from '../models/UnitsOfMeasure';
import { UpdateVendorDto, UpsertOrderDeliveryPairs, VendorData, VendorDto } from '../models/Vendor';
import { UpdateVendorItemDto, VendorItemBulkUpdateDto, VendorItemData, VendorItemDto } from '../models/VendorItem';
import {
  CogsDataGridResponse,
  CogsFiltersResponse,
  CogsKPICardsResponse,
  CogsRequest,
  LowStockReportRequest,
  LowStockReportResponse,
  GetAllModifierGroupsResponse,
  GetOneModifierResponse,
  InvoiceHistoryGLAccountDetailGridResults,
  InvoiceHistoryGLAccountDetailReportCardsResponse,
  InvoiceHistoryGLAccountDetailReportCommonRequest,
  InvoiceHistoryGLCardsRequest,
  InvoiceHistoryGLGridRequest,
  InvoiceHistoryGLKPIChartDataPoint,
  InvoiceHistoryGLKPINumbersResponse,
  InvoiceHistoryGLProcessedGridRow,
  PerpetualInventoryDataGridResponse,
  PerpetualInventoryKPICardsResponse,
  PerpetualInventoryRequest,
  InvoiceHistoryVendorDetailGridDataResponse,
  InvoiceHistoryVendorDetailReportCardsResponse,
  InvoiceHistoryVendorDetailReportCommonRequest,
  InvoiceHistoryVendorSummaryGridDataResponse,
  InvoiceHistoryVendorSummaryInvoiceAmountKpiResponse,
  InvoiceHistoryVendorSummaryInvoiceCountKpiResponse,
  InvoiceHistoryVendorSummaryReportCommonRequest,
  InvoiceTotalAmountByVendorResponse,
  RecipeItemUnitPriceRequest,
  RecipeItemUnitPriceResponse,
  Top20ItemUsageReportRequest,
  Top20ItemUsageReportResponse,
} from '../types';
import { flattenGlAccountCategory, OMIT_PARENT_SERVICE_ACCOUNT, STARTER_SERVICE_ITEMS } from '../utilities';

import { GlobalFilterBarDateInput } from '../../components/GlobalFilterBar/types';
import { BaseReportDataRequest, GlAccount, GlAccountsRequest, GlAccountsResponse } from '../../../api/sales/requests';
import SalesUtilities from '../../../sales/utilities';
import { useSites } from '../../contexts/siteContext';
import {
  CreatePurchaseOrderDto,
  CreatePurchaseOrderItemDto,
  CreateSuggestedPurchaseOrderDto,
  CreateSuggestedPurchaseOrderResponseDto,
  GeneratePoNumberResponseDto,
  PurchaseOrderDetails,
  PurchaseOrderItem,
  PurchaseOrdersGridRowResponse,
  UpdatePurchaseOrderItemDto,
  VendorItemPurchaseOrderDetails,
} from '../models/PurchaseOrders';
import { ValuationSettings } from '../models/ValuationSettings';
import { FoodCostAction } from '../RecipeDetailsPage/RecipeInformationForm/FoodCostChangeDialog';
import { ItemsOnHandQueries, RawItemsOnHandResponse } from '../models/RealTimeInventory';
import { CreateInvoiceTemplateDto, InvoiceTemplate, VendorTemplateFilterOptions } from '../models/InvoiceTemplate';

export interface VendorItemsListOptions {
  page?: number;
  limit?: number;
  active?: boolean;
  draft?: boolean;
  keyword?: string;
  addReceivingUnits?: boolean;
  addRawItems?: boolean;
  addLocations?: boolean;
  filters?: string[];
  sort?: string;
  filterLocationsBySite?: boolean;
  catalogIds?: string[];
}

export type InventoryRequests = {
  createRecipe: (recipeData: CreateRecipeDto) => Promise<CreateRecipeResponse>;
  cloneRecipe: (recipeId: string, recipeData: RecipeCloneDto) => Promise<CreateRecipeResponse>;
  createRecipeItem: (recipeItemData: RecipeItemData, foodCostAction?: FoodCostAction) => Promise<RecipeItemData>;
  updateRecipe: (
    id: string,
    RecipeData: UpdateRecipeDto,
    foodCostAction?: FoodCostAction
  ) => Promise<UpdateRecipeResponse>;
  deleteRecipe: (recipeId: string) => Promise<unknown>;
  updateRecipeItem: (
    id: string,
    recipeItemData: UpdateRecipeItemDto,
    foodCostAction?: FoodCostAction
  ) => Promise<Res.UpdateResponse>;
  bulkUpdateRecipeItems: (id: string, recipeItemsData: RecipeItemData[]) => Promise<Res.UpdateResponse>;
  deleteRecipeItem: (
    recipeItemId: string,
    foodCostAction?: FoodCostAction,
    recalculatedCostEffectiveDate?: Date
  ) => Promise<RecipeItemData>;
  getRecipes: (getRecipeIdOnly?: boolean) => Promise<RecipeDto[] | string[]>;
  findVendorsByName: (query?: string, active?: boolean, limit?: number, exactMatch?: boolean) => Promise<VendorData[]>;
  getVendorsPaginatedData: (
    page: number,
    limit: number,
    filters?: string[],
    sort?: string,
    active?: boolean,
    draft?: boolean
  ) => Promise<Paginated<VendorData>>;
  getVendorData: (vendorId: string) => Promise<VendorData | null>;
  createNonDraftVendor: (vendorDto: VendorDto) => Promise<VendorData>;
  createDraftVendor: (vendorDto: VendorDto) => Promise<VendorData>;
  getInvoicesPaginatedData: (
    page: number,
    limit: number,
    filters?: string[],
    sort?: string
  ) => Promise<Paginated<InvoiceData>>;
  getInvoicesByVendorData: (vendorId: string, status?: InvoiceStatus) => Promise<Paginated<InvoiceData>>;
  getInvoiceData: (invoiceId: string) => Promise<InvoiceData | null>;
  createInvoice: (invoiceDto: InvoiceDto) => Promise<InvoiceData>;
  updateInvoice: (invoiceId: string, invoiceDto: Partial<InvoiceDto>) => Promise<Res.UpdateResponse>;
  deleteInvoice: (invoiceId: string, isFinalized?: boolean) => Promise<void>;
  getInvoiceItemData: (
    invoiceId: string,
    invoiceItemId: string,
    addReceivingUnits: boolean,
    addLocations: boolean
  ) => Promise<InvoiceItemData>;
  getInvoiceItemsData: (
    invoiceId: string,
    addReceivingUnits: boolean,
    addLocations: boolean,
    errorHandler?: (error: unknown) => void
  ) => Promise<InvoiceItemData[] | undefined>;
  createInvoiceItemData: (
    invoiceId: string,
    invoiceItemDto: CreateInvoiceItemDto,
    errorHandler?: (error: unknown) => void
  ) => Promise<InvoiceItemData | null>;
  updateInvoiceItemData: (
    invoiceId: string,
    invoiceItemId: string,
    invoiceItemDto: UpdateInvoiceItemDto
  ) => Promise<Res.UpdateBody>;
  enableInvoiceEdit: (invoiceId: string) => Promise<unknown>;
  updateInvoiceStatus: (status: InvoiceStatusAction, id: string, newItems?: boolean) => Promise<unknown>;
  deleteInvoiceItem: (invoiceId: string, invoiceItemId: string) => Promise<void>;
  getVendorItemsData: (vendorId: string, options?: VendorItemsListOptions) => Promise<Paginated<VendorItemData>>;
  getVendorItemData: (
    vendorId: string,
    vendorItemId: string,
    errorHandler?: (error: unknown) => void
  ) => Promise<VendorItemData>;
  createDraftVendorItemData: (
    vendorId: string,
    vendorItemDto: VendorItemDto,
    errorHandler?: (error: unknown) => void
  ) => Promise<VendorItemData | null>;
  createNonDraftVendorItemData: (
    vendorId: string,
    vendorItemDto: VendorItemDto,
    errorHandler?: (error: unknown) => void
  ) => Promise<VendorItemData | null>;
  setVendorActive: (vendorId: string, active?: boolean) => Promise<Record<string, unknown>>;
  updateVendorItemData: (
    vendorId: string,
    vendorItemId: string,
    vendorItemDto: UpdateVendorItemDto,
    errorHandler?: (error: unknown) => void
  ) => Promise<Res.UpdateResponse>;
  updateNonDraftVendorItemData: (
    vendorId: string,
    vendorItemId: string,
    vendorItemDto: UpdateVendorItemDto,
    errorHandler?: (error: unknown) => void
  ) => Promise<Res.UpdateResponse>;
  bulkUpdateVendorItemMapping: (items: VendorItemBulkUpdateDto[]) => Promise<Res.UpdateResponse>;
  updateVendor: (vendorId: string, vendorDto: UpdateVendorDto) => Promise<Res.UpdateResponse>;
  updateVendorItemActiveStatus: (vendorId: string, vendorItemId: string, isActive: boolean) => Promise<unknown>;
  getServiceItemsData: () => Promise<GlAccount[]>;
  getContainerData: () => Promise<ContainerUnit[]>;
  getStartingUnitsData: () => Promise<ContainerUnit[]>;
  getUnitsData: () => Promise<IndustryUnit[]>;
  getAllUnitsData: () => Promise<IndustryUnit[]>;
  getCustomUnitsData: () => Promise<CustomUnit[] | null>;
  getCustomUnit: (customUnitId: string) => Promise<CustomUnit | null>;
  getCombinedUnitsData: (options: {
    page: number;
    limit: number;
    filters: string[];
    sortOrder: string;
    sortBy: string;
  }) => Promise<Paginated<CombinedUnit>>;
  getIsInUseData: (customUnitId: string) => Promise<boolean>;
  createCustomUnit: (customUnitDto: CustomUnitDto) => Promise<CustomUnit | undefined>;
  updateCustomUnitById: (customUnitId: string, customUnitDto: CustomUnitDto) => Promise<CustomUnit | undefined>;
  deleteCustomUnitById: (customUnitId: string) => Promise<string | void>;
  getReceivingUnitData: (receivingUnitId: string) => Promise<ReceivingUnit | null>;
  getAllReceivingUnitData: () => Promise<ReceivingUnit[] | null>;
  getAllReceivingUnitDataBySite: () => Promise<ReceivingUnit[] | null>;
  getRawItems: (options: {
    page: number;
    limit: number;
    find?: string;
    filters?: string[];
    active?: boolean;
    sort?: string;
  }) => Promise<Paginated<RawItemData>>;
  getRawItem: (rawItemId: string) => Promise<RawItemData>;
  calculateSiteUsagePer: (body: GlobalFilterBarDateInput) => Promise<string>;
  getUsagePerLastCalcDate: (siteId: string) => Promise<string>;
  validateRawItemCustomUnits: (rawItemId: string) => Promise<ValidateRawItemCustomUnitData>;
  updateRawItem: (rawItemId: string, rawItemDto: RawItemDto) => Promise<unknown>;
  updateRawItemActiveStatus: (rawItemId: string, isActive: boolean, futureDeactivationDate: string) => Promise<unknown>;
  updateReportingUnit: (rawItemId: string, reportingUnitId: string) => Promise<unknown>;
  updateRecipeUnit: (rawItemId: string, recipeUnitId: string) => Promise<unknown>;
  getReportingUnit: (rawItemId: string, euid: string) => Promise<UnitData>;
  getRecipeUnit: (rawItemId: string) => Promise<UnitData>;
  getUsagePer: (rawItemId: string, siteId: string) => Promise<number>;
  getDefaultUsagePerFactor: () => Promise<number>;
  updateUsagePer: (rawItemId: string, body: UpdateUsagePerData) => Promise<number>;
  createRawItem: (rawItemDto: RawItemDto) => Promise<RawItemData>;
  createRawItemNoDeactivation: (
    name: string | undefined,
    categoryId: string | undefined,
    isActive: boolean | undefined
  ) => Promise<RawItemData>;
  deleteRawItem: (rawItemId: string) => Promise<unknown>;
  getCostCalcMethodOptions: () => Promise<CostCalcMethodApiResponse>;
  mapCustomUnit: (customUnitMappingDto: CustomUnitMappingDto, rawItemId: string) => Promise<RawItemData>;
  mapIndustryUnit: (industryUnitId: InventoryUnitMappingDto, rawItemId: string) => Promise<RawItemData>;
  removeInventoryUnitMapping: (unitId: string, rawItemId: string) => Promise<unknown>;
  getGlCategories: (body: GlAccountsRequest) => Promise<GlAccountsResponse>;
  getRawItemMappedVendorItems: (
    rawItemId: string | undefined,
    options: {
      page: number;
      limit: number;
      onlyUnmapped?: boolean;
      addReceivingUnits?: boolean;
      addVendor?: boolean;
      addRawItems?: boolean;
      filters?: string[];
      sort?: string;
    }
  ) => Promise<Paginated<VendorItemData>>;
  getRawItemMappedVendorItemUnits: (options: {
    id: string;
    page: number;
    limit: number;
    find?: string;
    filter?: string;
    sort?: string;
  }) => Promise<VendorInventoryUnitsMap>;
  getRawItemMappedVendorItemUnitClasses: (rawItemId: string) => Promise<string[]>;
  isDuplicateError: string;
  getRawItemsByLocation: (
    locationId: string,
    options: {
      page: number;
      limit: number;
      getSelectedOnly?: boolean;
      filters?: string[];
      sortBy?: string;
      sortOrder?: SortDirection;
      includePrepItems?: boolean;
    }
  ) => Promise<LocationDetailResponse>;
  getInventorySummaryByLocation: (
    summaryId: string,
    locationId: string,
    options: {
      page: number;
      limit: number;
      searchValue?: string;
      includePrepItems?: boolean;
    }
  ) => Promise<LocationSummaryResponse>;
  createInventorySummary: (
    body: CreateInventorySummaryDto,
    includePrepItems?: boolean
  ) => Promise<InventorySummaryList>;
  updateLocationItemUnit: (locationId: string, body: UpdateLocationItemUnitDto[]) => Promise<unknown>;
  deleteLocationItem: (locationId: string, locationItemId: string) => Promise<unknown>;
  getInventoryLocations: (includePrepItems?: boolean) => Promise<LocationSummary>;
  bulkUpdateLocations: (body: { locations: LocationDto[] }) => Promise<Res.UpdateResponse>;
  getInventoryLists: (options: {
    page: number;
    limit: number;
    filters?: string[];
    sortBy?: string;
    sortOrder?: SortDirection;
    includePrepItems?: boolean;
  }) => Promise<Paginated<ListsSummaryData>>;
  updateInventoryList: (listId: string, body: UpdateListItemUnitDto[]) => Promise<unknown>;
  getItemsByInventoryList: (
    listId: string,
    showAssigned: boolean,
    options: {
      page: number;
      limit: number;
      filter?: string;
      sort?: string;
      includePrepItems?: boolean;
    }
  ) => Promise<Paginated<ListItem>>;
  getInventorySummary: (options: {
    page: number;
    limit: number;
    minValue?: number;
    maxValue?: number;
    minSkippedPercentage?: number;
    maxSkippedPercentage?: number;
    statuses?: MobileInventoryStatus[];
    startDate?: string | null;
    endDate?: string | null;
    sortBy?: string;
    includePrepItems?: boolean;
  }) => Promise<InventorySummaryResponse>;
  getInventorySummaryList: (id: string, includePrepItems?: boolean) => Promise<InventorySummaryList>;
  deleteInventorySummaryList: (id: string) => Promise<unknown>;
  getRealTimeInventoryOnHand: (options: ItemsOnHandQueries) => Promise<RawItemsOnHandResponse>;
  updateInventorySummaryLocation: (
    summaryId: string,
    locationId: string,
    body: UpdateLocationSummary,
    options?: {
      page?: number;
      limit?: number;
      searchValue?: string;
      includePrepItems?: boolean;
    }
  ) => Promise<unknown>;
  updateCountSummary: (summaryId: string, body: UpdateCountSummary, includePrepItems?: boolean) => Promise<unknown>;
  addOcrMetadata: (invoiceId: string, enterpriseUnit: string, ocrMetadataDto: OcrMetaDataDto) => Promise<unknown>;
  updateOcrMetadata: (
    invoiceId: string,
    enterpriseUnit: string,
    ocrMetadataDto: UpdateOcrMetaDataDto
  ) => Promise<unknown>;
  getVendorSummaryReportInvoiceCountCardData: (
    body: InvoiceHistoryVendorSummaryReportCommonRequest
  ) => Promise<InvoiceHistoryVendorSummaryInvoiceCountKpiResponse>;
  getVendorSummaryReportInvoiceAmountCardData: (
    body: InvoiceHistoryVendorSummaryReportCommonRequest
  ) => Promise<InvoiceHistoryVendorSummaryInvoiceAmountKpiResponse>;
  getInvoiceTotalAmountByVendorData: (
    body: InvoiceHistoryVendorSummaryReportCommonRequest
  ) => Promise<InvoiceTotalAmountByVendorResponse>;
  getVendorSummaryReportGridData: (
    body: InvoiceHistoryVendorSummaryReportCommonRequest
  ) => Promise<InvoiceHistoryVendorSummaryGridDataResponse>;
  getVendorDetailReportCardsData: (
    body: InvoiceHistoryVendorDetailReportCommonRequest
  ) => Promise<InvoiceHistoryVendorDetailReportCardsResponse>;
  getVendorDetailReportGridData: (
    body: InvoiceHistoryVendorDetailReportCommonRequest
  ) => Promise<InvoiceHistoryVendorDetailGridDataResponse>;
  getGLAccountReportGridData: (body: InvoiceHistoryGLGridRequest) => Promise<InvoiceHistoryGLProcessedGridRow[]>;
  getGLAccountReportKPINumbersData: (body: InvoiceHistoryGLCardsRequest) => Promise<InvoiceHistoryGLKPINumbersResponse>;
  getGLAccountReportKPIChartData: (body: InvoiceHistoryGLGridRequest) => Promise<InvoiceHistoryGLKPIChartDataPoint[]>;
  getGLAccountDetailReportCardsData: (
    body: InvoiceHistoryGLAccountDetailReportCommonRequest
  ) => Promise<InvoiceHistoryGLAccountDetailReportCardsResponse>;
  getGLAccountDetailReportGridData: (
    body: InvoiceHistoryGLAccountDetailReportCommonRequest
  ) => Promise<InvoiceHistoryGLAccountDetailGridResults[]>;
  getRecipesPaginatedData: (params: getRecipesPaginatedParams) => Promise<Paginated<RecipeRow>>;
  getRecipesFilterOptionsData: (
    params: getRecipesFilterPaginatedOptionsParams
  ) => Promise<RecipesFilterOptionsResponse>;
  getRecipesFilterOptionsByFieldData: (
    params: getRecipesFilterPaginatedOptionsParams
  ) => Promise<Partial<RecipesFilterOptionsResponse>>;
  getRecipeItemsPaginatedData: (
    recipeId: string,
    params: getRecipeItemsPaginatedParams
  ) => Promise<Paginated<RecipeItemRow>>;
  getRecipeData: (recipeId: string) => Promise<RecipeDto | null>;
  getUnassignedPrepAndRawItems: (
    id: string,
    filterPrepItem?: boolean,
    find?: string
  ) => Promise<GetAllSalesItemsMappingsResponse>;
  getAllSalesItems: () => Promise<GetAllSalesItemsResponse>;
  getOneSalesItem: (itemCode: string) => Promise<GetOneSalesItemsResponse>;
  getUnmappedSalesItems: (keyword?: string, limit?: number, page?: number) => Promise<GetUnmappedSalesItemsResponse>;
  createSalesItemMapping: (body: SalesItemMappingData) => Promise<MappingId>;
  updateSalesItemMapping: (id: string, body: EntityMappingData) => Promise<MappingId>;
  deleteSalesItemMapping: (id: string) => Promise<MappingId>;
  getAllRecipesAndRawItems: (filterPrepItem?: boolean, find?: string) => Promise<GetAllSalesItemsMappingsResponse>;
  getAllModifierGroups: () => Promise<GetAllModifierGroupsResponse>;
  getOneModifierGroup: (itemCode: string) => Promise<GetOneModifierResponse>;
  getRecipeItemUnitPrice: (
    recipeItemUnitPriceRequest: RecipeItemUnitPriceRequest
  ) => Promise<RecipeItemUnitPriceResponse>;
  getCogsFilters: (body: BaseReportDataRequest) => Promise<CogsFiltersResponse>;
  getCogsAvailableDates: (siteId: string) => Promise<string[]>;
  getCogsKPICards: (body: CogsRequest) => Promise<CogsKPICardsResponse>;
  getCogsDataGrid: (body: CogsRequest) => Promise<CogsDataGridResponse>;
  getTop20ItemUsageReportData: (body: Top20ItemUsageReportRequest) => Promise<Top20ItemUsageReportResponse>;
  getLowStockReportData: (body: LowStockReportRequest) => Promise<LowStockReportResponse>;
  createPurchaseOrder: (body: CreatePurchaseOrderDto) => Promise<PurchaseOrderDetails>;
  deletePurchaseOrder: (id: string) => Promise<unknown>;
  updatePurchaseOrder: (id: string, body: CreatePurchaseOrderDto) => Promise<PurchaseOrderDetails>;
  generatePoNumber: (vendorId: string) => Promise<GeneratePoNumberResponseDto>;
  getPurchaseOrdersGridData: (options: {
    page: number;
    limit: number;
    filters?: string[];
    sort?: string;
  }) => Promise<Paginated<PurchaseOrdersGridRowResponse>>;
  getPurchaseOrder: (id: string) => Promise<PurchaseOrderDetails>;
  createPurchaseOrderItem: (id: string, body: CreatePurchaseOrderItemDto) => Promise<PurchaseOrderItem>;
  deletePurchaseOrderItem: (purchaseOrderId: string, itemId: string) => Promise<unknown>;
  updatePurchaseOrderItem: (
    purchaseOrderId: string,
    itemId: string,
    body: UpdatePurchaseOrderItemDto
  ) => Promise<PurchaseOrderItem>;
  getPurchaseOrderItems: (
    id: string,
    options: { page: number; limit: number; filters?: string[]; sort?: string }
  ) => Promise<Paginated<PurchaseOrderItem>>;
  getPurchaseOrderItem: (purchaseOrderId: string, purchaseOrderItemId: string) => Promise<PurchaseOrderItem>;
  getPurchaseOrderVendorItem: (
    vendorId: string,
    vendorItemId: string,
    deliveryDate?: string
  ) => Promise<VendorItemPurchaseOrderDetails>;
  bulkDeletePurchaseOrderItems: (id: string, itemIds: string[]) => Promise<unknown>;
  createSuggestedPurchaseOrder: (
    request: CreateSuggestedPurchaseOrderDto
  ) => Promise<CreateSuggestedPurchaseOrderResponseDto>;
  sendPurchaseOrderEmail: (id: string, userEmail: string) => Promise<unknown>;
  sendPurchaseOrderFtp: (id: string) => Promise<unknown>;
  completePurchaseOrder: (id: string) => Promise<unknown>;
  updateVendorSftpPassword: (vendorId: string, password: string) => Promise<unknown>;
  getPerpetualInventoryKPICards: (body: PerpetualInventoryRequest) => Promise<PerpetualInventoryKPICardsResponse>;
  getPerpetualInventoryDataGrid: (body: PerpetualInventoryRequest) => Promise<PerpetualInventoryDataGridResponse>;
  upsertVendorOrderDeliveryPairs: (id: string, body: UpsertOrderDeliveryPairs[]) => Promise<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePurchaseOrderAcknowledgementStatus: (id: string, nepOrganization: string) => Promise<AxiosResponse<any, any>>;
  getValuationSettings: () => Promise<ValuationSettings>;
  updateValuationSettings: (valuationSettings: ValuationSettings) => Promise<ValuationSettings>;
  createInvoiceTemplate: (vendorId: string, body: CreateInvoiceTemplateDto) => Promise<InvoiceTemplate>;
  getInvoiceTemplates: (
    vendorIds: string[],
    templateName: string,
    page: number,
    limit: number
  ) => Promise<Paginated<InvoiceTemplate>>;
  getInvoiceTemplate: (vendorId: string, id: string) => Promise<InvoiceTemplate>;
  getVendorTemplateFilterOptions: () => Promise<VendorTemplateFilterOptions[]>;
};

const useInventoryRequests = (): InventoryRequests => {
  const { setSnackbarState } = useSnackbar();
  const { siteId: selectedSite, currentSiteId } = useCurrentSite();
  const { lastSelectedSite } = useSites();
  const { t: translate } = useTranslation();
  const callBsl = useCallBsl();
  const callBslAndHandleErrors = useCallBslAndHandleErrors();
  const isDuplicateError = 'isDuplicate';
  const bslSiteId = selectedSite === 'organizationView' ? '' : selectedSite;
  const siteId =
    selectedSite === 'organizationView' && lastSelectedSite?.enterpriseUnitId
      ? lastSelectedSite.enterpriseUnitId
      : selectedSite;
  const proxies = {
    vendors: 'vendors',
    uom: 'units-of-measure',
    recipes: 'recipes',
    category: 'gl-accounts',
    sales: 'sales',
    inventory: 'inventory',
    purchaseOrders: 'purchase-orders',
  };

  const Verb = Req.Firebase.HttpVerb;

  const setSubmittedSnackbar = useCallback(
    (message: string) => setSnackbarState({ open: true, color: 'success', message }),
    [setSnackbarState]
  );

  const handleBspError = useCallback((err: unknown, errorHandler?: (error: unknown) => void) => {
    if (errorHandler) {
      errorHandler(err);
    }
    // TODO: determine criteria for throwing error to err boundary versus snackbar, etc
    throw err;
  }, []);

  const getRouteOptions = ({
    page,
    limit,
    find,
    filter,
    filters,
    gridFilters,
    sort,
    sortBy,
    sortOrder,
    active,
    draft,
    addReceivingUnits,
    addLocations,
    addRawItems,
    exactMatch,
    getSelectedOnly,
    getRecipeIdOnly,
    filterPrepItem,
    minValue,
    maxValue,
    minSkippedPercentage,
    maxSkippedPercentage,
    statuses,
    startDate,
    endDate,
    searchValue,
    includePrepItems,
  }: {
    page?: number;
    limit?: number;
    find?: string;
    sort?: string;
    sortBy?: string;
    sortOrder?: SortDirection;
    filter?: string;
    filters?: string[];
    gridFilters?: string[];
    active?: boolean;
    draft?: boolean;
    addReceivingUnits?: boolean;
    addLocations?: boolean;
    addRawItems?: boolean;
    exactMatch?: boolean;
    getSelectedOnly?: boolean;
    getRecipeIdOnly?: boolean;
    filterPrepItem?: boolean;
    minValue?: number;
    maxValue?: number;
    minSkippedPercentage?: number;
    maxSkippedPercentage?: number;
    statuses?: MobileInventoryStatus[];
    startDate?: string | null;
    endDate?: string | null;
    searchValue?: string;
    includePrepItems?: boolean;
  }): Array<Req.Firebase.SingleQueryParam | Req.Firebase.MultiQueryParam> | undefined => {
    const params: Array<Req.Firebase.SingleQueryParam | Req.Firebase.MultiQueryParam> = [];

    if (page !== undefined) params.push({ kind: 'single', key: 'page', value: page.toString() });

    if (limit !== undefined) params.push({ kind: 'single', key: 'limit', value: limit.toString() });

    if (find !== undefined) params.push({ kind: 'single', key: 'find', value: find });

    // Wrap in quotes if contains comma
    const addMultiParam = (key: string, values?: string[]) => {
      if (values !== undefined) {
        const isSingleCommaValue = values.length === 1 && values[0].includes(',');
        params.push({
          kind: 'multi',
          key,
          value: isSingleCommaValue ? [`"${values[0]}"`] : values,
        });
      }
    };

    addMultiParam('filter', filters);
    addMultiParam('gridFilter', gridFilters);

    if (filter !== undefined) {
      const value = filter.includes(',') ? `"${filter}"` : filter;
      params.push({ kind: 'single', key: 'filter', value });
    }

    if (sort !== undefined) params.push({ kind: 'single', key: 'sort', value: sort });

    if (active !== undefined) params.push({ kind: 'single', key: 'active', value: active.toString() });

    if (draft !== undefined) params.push({ kind: 'single', key: 'draft', value: draft.toString() });

    if (addReceivingUnits !== undefined)
      params.push({ kind: 'single', key: 'addReceivingUnits', value: addReceivingUnits.toString() });

    if (addRawItems !== undefined) params.push({ kind: 'single', key: 'addRawItems', value: addRawItems.toString() });

    if (exactMatch !== undefined) params.push({ kind: 'single', key: 'exactMatch', value: exactMatch.toString() });

    if (sortBy !== undefined) params.push({ kind: 'single', key: 'sortBy', value: sortBy.toString() });

    if (sortOrder !== undefined)
      params.push({ kind: 'single', key: 'sortOrder', value: sortOrder.toString().toUpperCase() });

    if (getSelectedOnly !== undefined)
      params.push({ kind: 'single', key: 'getSelectedOnly', value: getSelectedOnly.toString() });

    if (getRecipeIdOnly !== undefined)
      params.push({ kind: 'single', key: 'getRecipeIdOnly', value: getRecipeIdOnly.toString() });

    if (filterPrepItem !== undefined)
      params.push({ kind: 'single', key: 'filterPrepItem', value: filterPrepItem.toString() });

    if (minValue !== undefined) params.push({ kind: 'single', key: 'minValue', value: minValue.toString() });
    if (maxValue !== undefined) params.push({ kind: 'single', key: 'maxValue', value: maxValue.toString() });
    if (minSkippedPercentage !== undefined)
      params.push({ kind: 'single', key: 'minSkippedPercentage', value: minSkippedPercentage.toString() });
    if (maxSkippedPercentage !== undefined)
      params.push({ kind: 'single', key: 'maxSkippedPercentage', value: maxSkippedPercentage.toString() });
    if (statuses !== undefined)
      params.push({ kind: 'multi', key: 'statuses', value: statuses.map((status) => status.toString()) });
    if (startDate !== null && startDate !== undefined && dayjs(startDate).isValid())
      params.push({ kind: 'single', key: 'startDate', value: startDate });
    if (endDate !== null && endDate !== undefined && dayjs(endDate).isValid())
      params.push({ kind: 'single', key: 'endDate', value: endDate });
    if (searchValue && searchValue !== '') params.push({ kind: 'single', key: 'searchValue', value: searchValue });

    if (addLocations !== undefined)
      params.push({ kind: 'single', key: 'addLocations', value: addLocations.toString() });

    if (includePrepItems !== undefined)
      params.push({ kind: 'single', key: 'includePrepItems', value: includePrepItems.toString() });

    if (params.length > 0) {
      return params;
    }

    return undefined;
  };

  /**
   * RECIPES
   */

  const createRecipe = useCallback(
    async (recipeData: CreateRecipeDto): Promise<CreateRecipeResponse> =>
      callBsl<CreateRecipeResponse>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['recipes'],
        payload: recipeData,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, handleBspError, callBsl]
  );

  const cloneRecipe = useCallback(
    async (recipeId: string, recipeData: RecipeCloneDto): Promise<CreateRecipeResponse> =>
      callBsl<CreateRecipeResponse>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['recipes', recipeId, 'clone'],
        payload: recipeData,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, handleBspError, callBsl]
  );

  const createRecipeItem = useCallback(
    async (recipeData: RecipeItemData, foodCostAction?: FoodCostAction): Promise<RecipeItemData> =>
      callBsl<RecipeItemData>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['recipes', 'recipeItem'],
        payload: recipeData,
        queryParams: foodCostAction ? [{ kind: 'single', key: 'foodCostAction', value: foodCostAction }] : undefined,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, handleBspError, callBsl]
  );

  const updateRecipe = useCallback(
    async (
      recipeId: string,
      recipeData: UpdateRecipeDto,
      foodCostAction?: FoodCostAction
    ): Promise<UpdateRecipeResponse> =>
      callBsl<UpdateRecipeResponse>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['recipes', recipeId],
        payload: recipeData,
        queryParams: foodCostAction ? [{ kind: 'single', key: 'foodCostAction', value: foodCostAction }] : undefined,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, handleBspError, callBsl]
  );

  const deleteRecipe = useCallback(
    async (recipeId: string) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.DELETE,
        pathSegments: ['recipes', recipeId],
      }).catch(handleBspError),
    [proxies.recipes, Verb.DELETE, handleBspError, callBsl]
  );

  const updateRecipeItem = useCallback(
    async (
      recipeItemId: string,
      recipeData: UpdateRecipeItemDto,
      foodCostAction?: FoodCostAction
    ): Promise<Res.UpdateResponse> =>
      callBsl<Res.UpdateResponse>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['recipes', 'recipeItem', recipeItemId],
        payload: recipeData,
        enterpriseUnit: siteId,
        queryParams: foodCostAction ? [{ kind: 'single', key: 'foodCostAction', value: foodCostAction }] : undefined,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const bulkUpdateRecipeItems = useCallback(
    async (recipeId: string, recipeData: RecipeItemData[]): Promise<Res.UpdateResponse> =>
      callBsl<Res.UpdateResponse>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['recipes', recipeId, 'recipeItems'],
        payload: recipeData,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, handleBspError, callBsl]
  );

  const deleteRecipeItem = useCallback(
    async (
      recipeItemId: string,
      foodCostAction?: FoodCostAction,
      recalculatedCostEffectiveDate?: Date
    ): Promise<RecipeItemData> => {
      const queryParams: Array<Req.Firebase.SingleQueryParam> = [];

      if (recalculatedCostEffectiveDate) {
        queryParams.push({
          kind: 'single',
          key: 'recalculatedCostEffectiveDate',
          value: recalculatedCostEffectiveDate.toISOString(),
        });
      }

      if (foodCostAction) {
        queryParams.push({
          kind: 'single',
          key: 'foodCostAction',
          value: foodCostAction,
        });
      }

      return callBsl<RecipeItemData>({
        proxy: proxies.recipes,
        verb: Verb.DELETE,
        pathSegments: ['recipes', 'recipeItem', recipeItemId],
        queryParams,
      })?.catch(handleBspError);
    },
    [proxies.recipes, Verb.DELETE, handleBspError, callBsl]
  );

  const getRecipes = useCallback(
    async (getRecipeIdOnly?: boolean): Promise<RecipeDto[] | string[]> =>
      callBsl<RecipeDto[] | string[]>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes'],
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
        queryParams: getRouteOptions({ getRecipeIdOnly }),
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, handleBspError, currentSiteId, callBsl]
  );

  const getRecipesPaginatedData = useCallback(
    async (params: getRecipesPaginatedParams): Promise<Paginated<RecipeRow>> => {
      const { page, limit, filters, gridFilters, sort, active, draft } = params;

      const paginatedData = await callBsl<Paginated<RecipeRow>>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes'],
        enterpriseUnit: siteId,
        queryParams: getRouteOptions({
          page,
          limit,
          filters,
          gridFilters,
          sort,
          active,
          draft,
        }),
      }).catch(handleBspError);
      return paginatedData;
    },
    [proxies.recipes, Verb.GET, handleBspError, siteId, callBsl]
  );

  const getRecipesFilterOptionsData = useCallback(
    async (params: getRecipesFilterPaginatedOptionsParams): Promise<RecipesFilterOptionsResponse> => {
      const { page, limit } = params;
      const paginatedData = await callBsl<RecipesFilterOptionsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes', 'filterOptions'],
        enterpriseUnit: siteId,
        queryParams: getRouteOptions({
          page,
          limit,
        }),
      }).catch(handleBspError);
      return paginatedData;
    },
    [proxies.recipes, Verb.GET, handleBspError, siteId, callBsl]
  );

  const getRecipesFilterOptionsByFieldData = useCallback(
    async (params: getRecipesFilterPaginatedOptionsParams): Promise<Partial<RecipesFilterOptionsResponse>> => {
      const { page, limit, field, value } = params;
      const queryParams = getRouteOptions({ page, limit }) ?? [];
      if (field && value) {
        queryParams.push({ kind: 'single', key: 'field', value: field });
        queryParams.push({ kind: 'single', key: 'value', value });
      }

      const paginatedData = await callBsl<RecipesFilterOptionsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes', 'filterOptions', 'find'],
        enterpriseUnit: siteId,
        queryParams,
      }).catch(handleBspError);
      return paginatedData;
    },
    [proxies.recipes, Verb.GET, handleBspError, siteId, callBsl]
  );

  const getRecipeItemsPaginatedData = useCallback(
    async (recipeId: string, params: getRecipeItemsPaginatedParams): Promise<Paginated<RecipeItemRow>> => {
      const { page, limit, filters, sort } = params;
      const paginatedData = await callBsl<Paginated<RecipeItemRow>>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes', recipeId, 'recipeItems'],
        queryParams: getRouteOptions({
          page,
          limit,
          filters,
          sort,
        }),
      }).catch(handleBspError);

      return paginatedData;
    },
    [proxies.recipes, Verb.GET, handleBspError, callBsl]
  );

  const getRecipeData = useCallback(
    async (recipeId: string): Promise<RecipeDto> =>
      callBsl<RecipeDto>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['recipes', recipeId],
        enterpriseUnit: siteId,
        queryParams: [{ kind: 'single', key: 'includeReferences', value: 'true' }],
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, handleBspError, siteId, callBsl]
  );

  const getUnassignedPrepAndRawItems = useCallback(
    async (id: string, filterPrepItem?: boolean, find?: string) =>
      callBsl<GetAllSalesItemsMappingsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        queryParams: getRouteOptions({ filterPrepItem, find, page: 1, limit: 100 }),
        pathSegments: ['recipes', id, 'recipeItems', 'unassigned'],
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, handleBspError, callBsl]
  );

  /**
   * VENDORS
   */
  const findVendorsByName = useCallback(
    async (find?: string, active?: boolean, limit?: number, exactMatch?: boolean): Promise<VendorData[]> =>
      callBsl<VendorData[]>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', 'findByName'],
        queryParams: getRouteOptions({ find, active, limit, exactMatch }),
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, currentSiteId, handleBspError, callBsl]
  );

  const getVendorsPaginatedData = useCallback(
    async (
      page: number,
      limit: number,
      filters?: string[],
      sort?: string,
      active?: boolean,
      draft?: boolean
    ): Promise<Paginated<VendorData>> =>
      callBsl<Paginated<VendorData>>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', 'paginated'],
        queryParams: getRouteOptions({
          page,
          limit,
          filters,
          sort,
          active,
          draft,
        }),
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, currentSiteId, handleBspError, callBsl]
  );

  const getVendorData = useCallback(
    async (vendorId: string): Promise<VendorData> =>
      callBsl<VendorData>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', vendorId],
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, currentSiteId, handleBspError, callBsl]
  );

  const createNonDraftVendor = useCallback(
    async (vendorDto: VendorDto): Promise<VendorData> =>
      callBsl<VendorData>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors'],
        payload: vendorDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const createDraftVendor = useCallback(
    async (vendorDto: VendorDto): Promise<VendorData> =>
      callBsl<VendorData>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors', 'draft'],
        payload: vendorDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateVendor = useCallback(
    async (vendorId: string, vendorDto: UpdateVendorDto): Promise<Res.UpdateResponse> => {
      const updateData = await callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        pathSegments: ['vendors', vendorId],
        verb: Verb.PATCH,
        payload: vendorDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
      return updateData;
    },
    [proxies.vendors, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const setVendorActive = useCallback(
    async (vendorId: string, active?: boolean): Promise<Record<string, unknown>> =>
      callBsl<Record<string, unknown>>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors', vendorId, 'active'],
        payload: { isActive: active },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  /**
   * INVOICES
   */

  const getInvoicesPaginatedData = useCallback(
    async (page: number, limit: number, filters?: string[], sort?: string): Promise<Paginated<InvoiceData>> =>
      callBsl<Paginated<InvoiceData>>({
        proxy: proxies.vendors,
        pathSegments: ['invoices', 'paginated'],
        queryParams: getRouteOptions({ page, limit, filters, sort }),
        verb: Verb.GET,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getInvoicesByVendorData = useCallback(
    async (vendorId: string, status?: InvoiceStatus): Promise<Paginated<InvoiceData>> =>
      callBsl<Paginated<InvoiceData>>({
        proxy: proxies.vendors,
        pathSegments: ['invoices', 'vendor', vendorId],
        queryParams: status ? [{ kind: 'single', key: 'status', value: status.toString() }] : undefined,
        verb: Verb.GET,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getInvoiceData = useCallback(
    async (invoiceId: string): Promise<InvoiceData | null> =>
      callBsl<InvoiceData | null>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['invoices', invoiceId],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const createInvoice = useCallback(
    async (invoiceDto: InvoiceDto): Promise<InvoiceData> =>
      callBsl<InvoiceData>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['invoices'],
        enterpriseUnit: siteId,
        payload: invoiceDto,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateInvoice = useCallback(
    async (invoiceId: string, invoiceDto: Partial<InvoiceDto>): Promise<Res.UpdateResponse> => {
      const updateData = await callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['invoices', invoiceId],
        payload: invoiceDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
      setSubmittedSnackbar(translate('inventory.successMessage.updateInvoice'));
      return updateData;
    },
    [proxies.vendors, Verb.PATCH, siteId, handleBspError, setSubmittedSnackbar, translate, callBsl]
  );

  const deleteInvoice = useCallback(
    async (invoiceId: string, finalized = false): Promise<void> => {
      const pathSegments = ['invoices', invoiceId];
      if (finalized) pathSegments.push('finalized');
      await callBsl({
        proxy: proxies.vendors,
        verb: Verb.DELETE,
        pathSegments,
        enterpriseUnit: siteId,
      })?.catch(handleBspError);
      setSubmittedSnackbar(translate('inventory.successMessage.deleteInvoice'));
    },
    [proxies.vendors, Verb.DELETE, siteId, handleBspError, setSubmittedSnackbar, translate, callBsl]
  );

  const enableInvoiceEdit = useCallback(
    async (invoiceId: string): Promise<unknown> =>
      callBsl({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['invoices', invoiceId, 'edit'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateInvoiceStatus = useCallback(
    async (status: InvoiceStatusAction, id: string, newItems = false): Promise<unknown> => {
      const pathSegments = ['invoices', id];
      if (status === InvoiceStatusAction.Accepted) {
        pathSegments.push('accept');
        if (newItems) pathSegments.push('newItems');
      } else if (status === InvoiceStatusAction.Finalized) {
        pathSegments.push('finalize');
        if (newItems) pathSegments.push('newItems');
      } else if (status === InvoiceStatusAction.Unfinalized) {
        pathSegments.push('unfinalize');
      } else {
        throw new TypeError(`Unsupported invoice status: ${status}`);
      }
      const res = await callBsl({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments,
        enterpriseUnit: siteId,
      })?.catch(handleBspError);
      setSubmittedSnackbar(translate('inventory.successMessage.updateInvoiceStatus'));
      return res;
    },
    [proxies.vendors, Verb.POST, siteId, handleBspError, setSubmittedSnackbar, translate, callBsl]
  );

  /**
   * INVOICE ITEMS
   */

  const getInvoiceItemData = useCallback(
    async (
      invoiceId: string,
      invoiceItemId: string,
      addReceivingUnits: boolean,
      addLocations: boolean
    ): Promise<InvoiceItemData> =>
      callBsl<InvoiceItemData>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['invoices', invoiceId, 'items', invoiceItemId],
        queryParams: [
          { kind: 'single', key: 'addReceivingUnits', value: addReceivingUnits.toString() },
          { kind: 'single', key: 'addLocations', value: addLocations.toString() },
        ],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getInvoiceItemsData = useCallback(
    async (
      invoiceId: string,
      addReceivingUnits: boolean,
      addLocations: boolean,
      errorHandler?: (error: unknown) => void
    ): Promise<InvoiceItemData[] | undefined> => {
      const pathSegments = ['invoices', invoiceId, 'items', 'paginated'];

      if (errorHandler) {
        return callBslAndHandleErrors<Paginated<InvoiceItemData>, void>(
          {
            proxy: proxies.vendors,
            verb: Verb.GET,
            pathSegments,
            queryParams: getRouteOptions({
              page: 1,
              limit: 500,
              addReceivingUnits,
              addLocations,
            }),
            enterpriseUnit: siteId,
          },
          errorHandler
        ).then((response) => response?.items);
      }

      const request = await callBsl<Paginated<InvoiceItemData>>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments,
        queryParams: getRouteOptions({
          page: 1,
          limit: 500,
          addReceivingUnits,
          addLocations,
        }),
        enterpriseUnit: siteId,
      }).catch(handleBspError);
      return request.items;
    },
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl, callBslAndHandleErrors]
  );

  const createInvoiceItemData = useCallback(
    async (invoiceId: string, invoiceItemDto: CreateInvoiceItemDto): Promise<InvoiceItemData | null> =>
      callBsl<InvoiceItemData | null>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['invoices', invoiceId, 'items'],
        payload: invoiceItemDto,
        enterpriseUnit: siteId,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err);
      }),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateInvoiceItemData = useCallback(
    async (invoiceId: string, invoiceItemId: string, invoiceItemDto: UpdateInvoiceItemDto): Promise<Res.UpdateBody> =>
      callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['invoices', invoiceId, 'items', invoiceItemId],
        payload: invoiceItemDto,
        enterpriseUnit: siteId,
      })
        .then(({ body }) => body)
        .catch((err) => {
          if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
            // components will handle duplicate errors without the error boundary
            throw err;
          }
          return handleBspError(err);
        }),
    [proxies.vendors, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const deleteInvoiceItem = useCallback(
    async (invoiceId: string, invoiceItemId: string): Promise<void> =>
      callBsl<void>({
        proxy: proxies.vendors,
        verb: Verb.DELETE,
        pathSegments: ['invoices', invoiceId, 'items', invoiceItemId],
        enterpriseUnit: siteId,
      })?.catch(handleBspError),
    [proxies.vendors, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  /**
   * VENDOR ITEMS
   */

  const getVendorItemsData = useCallback(
    async (vendorId: string, options?: VendorItemsListOptions): Promise<Paginated<VendorItemData>> => {
      const {
        active,
        draft,
        keyword,
        addReceivingUnits,
        addRawItems,
        addLocations,
        page,
        limit,
        sort,
        filters,
        filterLocationsBySite,
      } = options ?? {};
      return callBsl<Paginated<VendorItemData>>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', vendorId, 'items'],
        enterpriseUnit: filterLocationsBySite ? siteId : undefined,
        queryParams: getRouteOptions({
          page,
          limit,
          active,
          draft,
          find: keyword,
          addReceivingUnits,
          addLocations,
          addRawItems,
          sort,
          filters,
        }),
      }).catch(handleBspError);
    },
    [proxies.vendors, Verb.GET, handleBspError, callBsl, siteId]
  );

  const getVendorItemData = useCallback(
    async (vendorId: string, vendorItemId: string, errorHandler?: (error: unknown) => void): Promise<VendorItemData> =>
      callBsl<VendorItemData>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', vendorId, 'items', vendorItemId],
        enterpriseUnit: siteId,
      })?.catch((err) => handleBspError(err, errorHandler)),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const createDraftVendorItemData = useCallback(
    async (
      vendorId: string,
      vendorItemDto: VendorItemDto,
      errorHandler?: (error: unknown) => void
    ): Promise<VendorItemData | null> =>
      callBsl<VendorItemData | null>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors', vendorId, 'items', 'draft'],
        payload: vendorItemDto,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err, errorHandler);
      }),
    [proxies.vendors, Verb.POST, handleBspError, callBsl]
  );

  const createNonDraftVendorItemData = useCallback(
    async (
      vendorId: string,
      vendorItemDto: VendorItemDto,
      errorHandler?: (error: unknown) => void
    ): Promise<VendorItemData | null> =>
      callBsl<VendorItemData | null>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors', vendorId, 'items'],
        payload: vendorItemDto,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err, errorHandler);
      }),
    [proxies.vendors, Verb.POST, handleBspError, callBsl]
  );

  const updateVendorItemData = useCallback(
    async (
      vendorId: string,
      vendorItemId: string,
      vendorItemDto: UpdateVendorItemDto,
      errorHandler?: (error: unknown) => void
    ): Promise<Res.UpdateResponse> =>
      // Only draft vendor items can have receiving unit changed
      // Draft -> VendorItemDto, Non-draft -> UpdateVendorItemDto
      callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['vendors', vendorId, 'items', vendorItemId, 'draft'],
        payload: vendorItemDto,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err, errorHandler);
      }),
    [proxies.vendors, Verb.PATCH, handleBspError, callBsl]
  );

  const updateNonDraftVendorItemData = useCallback(
    async (
      vendorId: string,
      vendorItemId: string,
      vendorItemDto: UpdateVendorItemDto,
      errorHandler?: (error: unknown) => void
    ): Promise<Res.UpdateResponse> =>
      // Only draft vendor items can have receiving unit changed
      // Draft -> VendorItemDto, Non-draft -> UpdateVendorItemDto
      callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['vendors', vendorId, 'items', vendorItemId, 'nondraft'],
        payload: vendorItemDto,
      }).catch((err) => handleBspError(err, errorHandler)),
    [proxies.vendors, Verb.PATCH, handleBspError, callBsl]
  );

  const bulkUpdateVendorItemMapping = useCallback(
    async (items: VendorItemBulkUpdateDto[]): Promise<Res.UpdateResponse> =>
      callBsl<Res.UpdateResponse>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['vendors', 'items', 'bulkRawItemUpdate'],
        payload: { items },
      }).catch(handleBspError),
    [proxies.vendors, Verb.PATCH, handleBspError, callBsl]
  );

  const updateVendorItemActiveStatus = useCallback(
    async (vendorId: string, vendorItemId: string, isActive: boolean): Promise<Record<string, unknown>> =>
      callBsl<Record<string, unknown>>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendors', vendorId, 'items', vendorItemId, 'active'],
        payload: { isActive },
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, handleBspError, callBsl]
  );

  /**
   * UNITS OF MEASURE
   */

  const getContainerData = useCallback(
    async (): Promise<ContainerUnit[]> =>
      callBsl<{ data: ContainerUnit[]; items: ContainerUnit[] }>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['containerUnits'],
      })
        .then(({ data, items }) => (data as ContainerUnit[]) ?? (items as ContainerUnit[]))
        .catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getStartingUnitsData = useCallback(
    async (): Promise<ContainerUnit[]> =>
      callBsl<{ data: ContainerUnit[]; items: ContainerUnit[] }>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['containerUnits', 'startingUnits'],
        queryParams: [{ kind: 'single', key: 'lang', value: getStoredLang() }],
      })
        .then(({ data, items }) => (data as ContainerUnit[]) ?? (items as ContainerUnit[]))
        .catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getUnitsData = useCallback(
    async (): Promise<IndustryUnit[]> =>
      callBsl<{ data: IndustryUnit[]; items: IndustryUnit[] }>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['industryUnits', 'byCategory'],
        queryParams: [{ kind: 'single', key: 'filterBy', value: 'isReceiveUnit' }],
      })
        .then(({ data, items }) => (data as IndustryUnit[]) ?? (items as IndustryUnit[]))
        .catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getAllUnitsData = useCallback(
    async (): Promise<IndustryUnit[]> =>
      callBsl<{ data: IndustryUnit[]; items: IndustryUnit[] }>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['industryUnits', 'byClass'],
        queryParams: [{ kind: 'single', key: 'lang', value: getStoredLang() }],
      })
        .then(({ data, items }) => (data as IndustryUnit[]) ?? (items as IndustryUnit[]))
        .catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getReceivingUnitData = useCallback(
    async (receivingUnitId: string): Promise<ReceivingUnit | null> =>
      callBsl<ReceivingUnit | null>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['receivingUnits', receivingUnitId],
      }).catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getAllReceivingUnitDataBySite = useCallback(
    async (): Promise<ReceivingUnit[] | null> =>
      callBsl<ReceivingUnit[] | null>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['receivingUnits'],
        enterpriseUnit: siteId,
        queryParams: [{ kind: 'single', key: 'lang', value: getStoredLang() }],
      }).catch(handleBspError),
    [proxies.uom, siteId, Verb.GET, handleBspError, callBsl]
  );

  const getAllReceivingUnitData = useCallback(
    async (): Promise<ReceivingUnit[] | null> =>
      callBsl<ReceivingUnit[] | null>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['receivingUnits'],
      }).catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getCustomUnitsData = useCallback(
    async (): Promise<CustomUnit[] | null> =>
      callBsl<CustomUnit[] | null>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['customUnits'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl, siteId]
  );

  const getIsInUseData = useCallback(
    async (customUnitId: string): Promise<boolean> =>
      callBsl<boolean>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['customUnits', customUnitId],
      })
        .then(() => false)
        .catch((err) => {
          handleBspError(err);
          return false;
        }),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const getCustomUnit = useCallback(
    async (customUnitId: string): Promise<CustomUnit | null> =>
      callBsl<CustomUnit>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['customUnits', customUnitId],
      }).catch(handleBspError),
    [proxies.uom, Verb.GET, handleBspError, callBsl]
  );

  const deleteCustomUnitById = (customUnitId: string): Promise<string | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.uom,
        verb: Verb.DELETE,
        pathSegments: ['customUnits', customUnitId],
      },
      handleBspError
    );

  const createCustomUnit = useCallback(
    async (customUnitDto: CustomUnitDto): Promise<CustomUnit | undefined> =>
      callBsl<CustomUnit | undefined>({
        proxy: proxies.uom,
        verb: Verb.POST,
        pathSegments: ['customUnits'],
        payload: customUnitDto,
        enterpriseUnit: siteId,
      }),
    [proxies.uom, Verb.POST, siteId, callBsl]
  );

  const updateCustomUnitById = useCallback(
    async (customUnitId: string, customUnitDto: CustomUnitDto): Promise<CustomUnit | undefined> =>
      callBsl<CustomUnit | undefined>({
        proxy: proxies.uom,
        verb: Verb.PUT,
        pathSegments: ['customUnits', customUnitId],
        payload: customUnitDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.uom, Verb.PUT, siteId, handleBspError, callBsl]
  );

  const getCombinedUnitsData = useCallback(
    async (options: { page: number; limit: number; filters: string[]; sortOrder: string; sortBy: string }) => {
      const queryParams: Array<Req.Firebase.SingleQueryParam | Req.Firebase.MultiQueryParam> = [];
      queryParams?.push({ kind: 'single', key: 'page', value: options.page.toString() });
      queryParams?.push({ kind: 'single', key: 'limit', value: options.limit.toString() });
      queryParams?.push({ kind: 'single', key: 'sortOrder', value: options.sortOrder });
      queryParams?.push({ kind: 'single', key: 'sortBy', value: options.sortBy });
      queryParams?.push({ kind: 'single', key: 'lang', value: getStoredLang() });
      queryParams?.push({ kind: 'multi', key: 'filter[]', value: options.filters });
      return callBsl<Paginated<CombinedUnit>>({
        proxy: proxies.uom,
        verb: Verb.GET,
        pathSegments: ['allUnits'],
        enterpriseUnit: siteId,
        queryParams,
      }).catch(handleBspError);
    },
    [proxies.uom, Verb.GET, siteId, handleBspError, callBsl]
  );
  /**
   * RAW ITEMS
   */

  const getRawItems = useCallback(
    async (options: { page: number; limit: number; find?: string; active?: boolean }) =>
      callBsl<Paginated<RawItemData>>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems'],
        queryParams: getRouteOptions(options),
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, currentSiteId, handleBspError, callBsl]
  );

  const getRawItem = useCallback(
    async (rawItemId: string) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', rawItemId],
        enterpriseUnit: bslSiteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, bslSiteId, handleBspError, callBsl]
  );

  const calculateSiteUsagePer = useCallback(
    async (body: GlobalFilterBarDateInput): Promise<string> =>
      callBsl<string>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['rawItems', 'calculateSiteUsagePer'],
        enterpriseUnit: bslSiteId,
        payload: { ...body, siteId },
      }).catch(handleBspError),
    [callBsl, proxies.recipes, Verb.POST, bslSiteId, handleBspError, siteId]
  );

  const getUsagePerLastCalcDate = useCallback(
    async (): Promise<string> =>
      callBsl<string>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', 'last-calculated-usage-per-date'],
        enterpriseUnit: bslSiteId,
        queryParams: [{ kind: 'single', key: 'siteId', value: siteId || '' }],
      }).catch(handleBspError),
    [callBsl, proxies.recipes, Verb.GET, bslSiteId, siteId, handleBspError]
  );

  const validateRawItemCustomUnits = useCallback(
    async (rawItemId: string) =>
      callBsl<ValidateRawItemCustomUnitData>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', 'validateRawItemCustomUnits', rawItemId],
        enterpriseUnit: bslSiteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, bslSiteId, handleBspError, callBsl]
  );

  const updateRawItem = useCallback(
    async (rawItemId: string, rawItemDto: RawItemDto) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId],
        payload: rawItemDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const updateRawItemActiveStatus = useCallback(
    async (rawItemId: string, isActive: boolean, futureDeactivationDate: string) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['rawItems', rawItemId, 'active'],
        payload: { isActive, ...(futureDeactivationDate && { futureDeactivationDate }) },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateReportingUnit = useCallback(
    async (rawItemId: string, reportingUnitId: string) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId, 'reportingUnit'],
        payload: { unitId: reportingUnitId },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const updateRecipeUnit = useCallback(
    async (rawItemId: string, recipeUnitId: string) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId, 'recipeUnit'],
        payload: { unitId: recipeUnitId },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const getReportingUnit = useCallback(
    async (rawItemId: string, euid: string): Promise<UnitData> =>
      callBsl<UnitData>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', rawItemId, 'reportingUnit'],
        enterpriseUnit: euid === 'organizationView' ? siteId : euid,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, handleBspError, callBsl, siteId]
  );

  const getRecipeUnit = useCallback(
    async (rawItemId: string): Promise<UnitData> =>
      callBsl<UnitData>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', rawItemId, 'recipeUnit'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const createRawItem = useCallback(
    async (rawItemDto: RawItemDto) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['rawItems'],
        payload: rawItemDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, siteId, handleBspError, callBsl]
  );

  const createRawItemNoDeactivation = useCallback(
    async (name: string | undefined, categoryId: string | undefined, isActive: boolean | undefined) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['rawItems'],
        payload: {
          name,
          categoryId,
          isActive,
        },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, siteId, handleBspError, callBsl]
  );

  const deleteRawItem = useCallback(
    async (rawItemId: string) =>
      callBsl({
        proxy: proxies.recipes,
        verb: Verb.DELETE,
        pathSegments: ['rawItems', rawItemId],
        payload: null,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [Verb.DELETE, handleBspError, proxies.recipes, siteId, callBsl]
  );

  const getCostCalcMethodOptions = useCallback(
    async () =>
      callBsl<CostCalcMethodApiResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', 'cost-calculation-methods'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const mapCustomUnit = useCallback(
    async (customUnitMappingDto: CustomUnitMappingDto, rawItemId: string) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId, 'customUnitMapping'],
        payload: customUnitMappingDto,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const mapIndustryUnit = useCallback(
    async (industryUnitId: InventoryUnitMappingDto, rawItemId: string) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId, 'industryUnitMapping'],
        payload: industryUnitId,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const removeInventoryUnitMapping = useCallback(
    async (inventoryUnitId: string, rawItemId: string) =>
      callBsl<RawItemData>({
        proxy: proxies.recipes,
        verb: Verb.PATCH,
        pathSegments: ['rawItems', rawItemId, 'inventoryUnitMapping', inventoryUnitId, 'false'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  /**
   * GL Categories
   */

  const getGlCategories = useCallback(
    async (body: GlAccountsRequest) =>
      callBsl<GlAccountsResponse>({
        proxy: proxies.category,
        verb: Verb.GET,
        pathSegments: ['gl-accounts'],
        queryParams:
          // If all the arrays are empty it causes a weird authentication error, so avoid that.
          body.isActive ||
          body.categoryIds.length > 0 ||
          body.accountTypes.length > 0 ||
          body.accountDetailTypes.length > 0
            ? SalesUtilities.toGetGlAccountsQueryParams(body)
            : undefined,
      }).catch(handleBspError),
    [proxies.category, Verb.GET, handleBspError, callBsl]
  );

  /**
   * SERVICE ITEMS
   */
  const getServiceItemsData = async (organizationId?: string): Promise<GlAccount[]> => {
    // TODO: Add entire 8000 category in core (starter only requires 8100)
    const req: GlAccountsRequest = {
      isActive: '',
      categoryIds: [5, 6, 7],
      accountTypes: [],
      accountDetailTypes: [],
    };

    const glAccounts = (await getGlCategories(req)).accounts;

    const flattenedCategories = glAccounts?.map((glAccount) => flattenGlAccountCategory(glAccount));

    const starterCategories: GlAccount[] = [];
    flattenedCategories?.forEach((flatCat) => {
      flatCat?.forEach((account) => {
        if (
          account &&
          account.accountNumber &&
          STARTER_SERVICE_ITEMS.indexOf(account.accountNumber.toString()) !== -1 &&
          OMIT_PARENT_SERVICE_ACCOUNT.indexOf(account.accountNumber.toString()) === -1
        ) {
          starterCategories.push(account);
        }
      });
    });

    return starterCategories;
  };

  const getRawItemMappedVendorItems = useCallback(
    async (
      rawItemId: string | undefined,
      options: {
        page: number;
        limit: number;
        onlyUnmapped?: boolean;
        addReceivingUnits?: boolean;
        addVendor?: boolean;
        addRawItems?: boolean;
        filters?: string[];
        sort?: string;
      }
    ) => {
      const { page, limit, addReceivingUnits, addVendor, onlyUnmapped, addRawItems } = options;
      const queryParams: Array<Req.Firebase.SingleQueryParam | Req.Firebase.MultiQueryParam> = [];

      if (rawItemId) queryParams?.push({ kind: 'single', key: 'rawItemId', value: rawItemId });
      queryParams?.push({ kind: 'single', key: 'limit', value: limit.toString() });
      queryParams?.push({ kind: 'single', key: 'page', value: page.toString() });
      queryParams?.push({ kind: 'single', key: 'addReceivingUnits', value: addReceivingUnits?.toString() ?? 'false' });
      queryParams?.push({ kind: 'single', key: 'addVendor', value: addVendor?.toString() ?? 'false' });
      queryParams?.push({ kind: 'single', key: 'addRawItems', value: addRawItems?.toString() ?? 'false' });
      queryParams?.push({ kind: 'multi', key: 'filter[]', value: options.filters ?? [] });
      if (options.sort !== undefined) queryParams.push({ kind: 'single', key: 'sort', value: options.sort });
      if (isBoolean(onlyUnmapped)) {
        queryParams?.push({
          kind: 'single',
          key: 'onlyUnmapped',
          value: onlyUnmapped.toString(),
        });
      }

      return callBsl<Paginated<VendorItemData>>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', 'items'],
        queryParams,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
    },
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getRawItemMappedVendorItemUnitClasses = useCallback(
    async (rawItemId: string) => {
      const queryParams: Array<Req.Firebase.SingleQueryParam | Req.Firebase.MultiQueryParam> = [];
      queryParams.push({ kind: 'single', key: 'rawItemId', value: rawItemId });
      return callBsl<string[]>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendors', 'items', 'unitClasses'],
        queryParams,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
    },
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );
  const getRawItemMappedVendorItemUnits = useCallback(
    async (options: { id: string; page: number; limit: number; find?: string; filter?: string; sort?: string }) => {
      const { id, page, limit, find, filter, sort } = options;
      const queryParams = toQueryParams({
        limit: limit.toString(),
        page: page.toString(),
        find,
        filter,
        sort,
      });
      return callBsl<VendorInventoryUnitsMap>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', id, 'unitsAndItems'],
        queryParams,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
    },
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const addOcrMetadata = useCallback(
    async (
      invoiceId: string,
      enterpriseUnit: string,
      ocrMetadataDto: OcrMetaDataDto,
      errorHandler?: (error: unknown) => void
    ): Promise<VendorItemData | null> =>
      callBsl<VendorItemData | null>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['ocr', invoiceId],
        payload: ocrMetadataDto,
        enterpriseUnit,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err, errorHandler);
      }),
    [proxies.vendors, Verb.POST, handleBspError, callBsl]
  );

  const updateOcrMetadata = useCallback(
    async (
      invoiceId: string,
      enterpriseUnit: string,
      ocrMetadataDto: UpdateOcrMetaDataDto,
      errorHandler?: (error: unknown) => void
    ): Promise<VendorItemData | null> =>
      callBsl<VendorItemData | null>({
        proxy: proxies.vendors,
        verb: Verb.PATCH,
        pathSegments: ['ocr', invoiceId],
        payload: ocrMetadataDto,
        enterpriseUnit,
      }).catch((err) => {
        if (checkFirebaseErrorCodeExists(err as HttpsError, 'functions/already-exists')) {
          // components will handle duplicate errors without the error boundary
          throw err;
        }
        return handleBspError(err, errorHandler);
      }),
    [proxies.vendors, Verb.PATCH, handleBspError, callBsl]
  );

  /**
   * INVOICE HISTORY REPORTS
   */

  const getVendorSummaryReportInvoiceCountCardData = useCallback(
    async (body: InvoiceHistoryVendorSummaryReportCommonRequest) =>
      callBsl<InvoiceHistoryVendorSummaryInvoiceCountKpiResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'kpi-cards', 'invoice-count'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          invoiceCountComparisonPercent: 0,
          invoiceCountTotal: 0,
          invoiceCountComparison: 0,
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getVendorSummaryReportInvoiceAmountCardData = useCallback(
    async (body: InvoiceHistoryVendorSummaryReportCommonRequest) =>
      callBsl<InvoiceHistoryVendorSummaryInvoiceAmountKpiResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'kpi-cards', 'invoice-amount'], // needs to be updated
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          totalInvoiceAmountDigits: 0,
          totalInvoiceAmountComparisonPercent: 0,
          totalInvoiceAmount: 0,
          totalInvoiceAmountComparison: 0,
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getInvoiceTotalAmountByVendorData = useCallback(
    async (body: InvoiceHistoryVendorSummaryReportCommonRequest) =>
      callBsl<InvoiceTotalAmountByVendorResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'kpi-cards', 'total-by-vendor'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          invoiceTotalByVendor: [],
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getVendorSummaryReportGridData = useCallback(
    async (body: InvoiceHistoryVendorSummaryReportCommonRequest) =>
      callBsl<InvoiceHistoryVendorSummaryGridDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'grid-data'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          results: [],
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getVendorDetailReportCardsData = useCallback(
    async (body: InvoiceHistoryVendorDetailReportCommonRequest) =>
      callBsl<InvoiceHistoryVendorDetailReportCardsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'vendor-details', 'kpi-cards'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          count: 0,
          amountUnfinalized: 0,
          totalAmount: 0,
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getVendorDetailReportGridData = useCallback(
    async (body: InvoiceHistoryVendorDetailReportCommonRequest) =>
      callBsl<InvoiceHistoryVendorDetailGridDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history-vendors', 'vendor-details', 'grid-data'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          results: [],
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getGLAccountReportGridData = useCallback(
    async (body: InvoiceHistoryGLGridRequest) =>
      callBsl<InvoiceHistoryGLProcessedGridRow[]>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history', 'by-category'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return [] as InvoiceHistoryGLProcessedGridRow[];
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getGLAccountReportKPINumbersData = useCallback(
    async (body: InvoiceHistoryGLCardsRequest) =>
      callBsl<InvoiceHistoryGLKPINumbersResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history', 'by-category', 'kpi-numbers'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          totalInvoiceCountCurrent: 0,
          totalInvoiceCountPrevious: 0,
          invoiceCountComparisonPercentage: 0,
          totalInvoiceAmountCurrent: 0,
          totalInvoiceAmountPrevious: 0,
          invoiceAmountComparisonPercentage: 0,
        };
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getGLAccountReportKPIChartData = useCallback(
    async (body: InvoiceHistoryGLGridRequest) =>
      callBsl<InvoiceHistoryGLKPIChartDataPoint[]>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history', 'by-category', 'kpi-chart'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return [] as InvoiceHistoryGLKPIChartDataPoint[];
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getGLAccountDetailReportCardsData = useCallback(
    async (body: InvoiceHistoryGLAccountDetailReportCommonRequest) =>
      callBsl<InvoiceHistoryGLAccountDetailReportCardsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history', 'by-category', 'details', 'kpi-numbers'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return {
          invoiceCount: 0,
          invoiceUnFinalized: 0,
          invoiceAmount: 0,
        } as InvoiceHistoryGLAccountDetailReportCardsResponse;
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  const getGLAccountDetailReportGridData = useCallback(
    async (body: InvoiceHistoryGLAccountDetailReportCommonRequest) =>
      callBsl<InvoiceHistoryGLAccountDetailGridResults[]>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['invoice-history', 'by-category', 'details', 'grid-data'],
        payload: body,
      }).catch((err) => {
        handleBspError(err);
        return [];
      }),
    [callBsl, proxies.sales, Verb.POST, handleBspError]
  );

  /**
   * INVENTORY LOCATIONS
   */

  const getRawItemsByLocation = useCallback(
    async (
      locationId: string,
      options: {
        page: number;
        limit: number;
        getSelectedOnly?: boolean;
        filters?: string[];
        sortBy?: string;
        sortOrder?: SortDirection;
        includePrepItems?: boolean;
      }
    ) =>
      callBsl<LocationDetailResponse>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: ['location', locationId],
        queryParams: getRouteOptions(options),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  const updateLocationItemUnit = useCallback(
    async (locationId: string, body: UpdateLocationItemUnitDto[]) =>
      callBsl({
        proxy: proxies.inventory,
        verb: Verb.POST,
        pathSegments: ['location', locationId, 'items'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.POST, siteId, handleBspError, callBsl]
  );

  const deleteLocationItem = useCallback(
    async (locationId: string, locationItemId: string) =>
      callBsl({
        proxy: proxies.inventory,
        verb: Verb.DELETE,
        pathSegments: ['location', locationId, 'locationItem', locationItemId],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const getInventoryLocations = useCallback(
    async (includePrepItems?: boolean) =>
      callBsl<LocationSummary>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: ['location'],
        queryParams: getRouteOptions({ includePrepItems }),
        enterpriseUnit: currentSiteId === 'organizationView' ? '' : currentSiteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, currentSiteId, handleBspError, callBsl]
  );

  const bulkUpdateLocations = useCallback(
    async (body: { locations: LocationDto[] }): Promise<Res.UpdateResponse> =>
      callBsl<Res.UpdateResponse>({
        proxy: proxies.inventory,
        verb: Verb.POST,
        pathSegments: ['location-overrides', 'update-locations'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.POST, siteId, handleBspError, callBsl]
  );

  const getInventoryLists = useCallback(
    async (options: {
      page: number;
      limit: number;
      filters?: string[];
      sortBy?: string;
      sortOrder?: SortDirection;
      includePrepItems?: boolean;
    }) =>
      callBsl<Paginated<ListsSummaryData>>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: ['inventory-lists'],
        queryParams: getRouteOptions(options),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  const updateInventoryList = useCallback(
    async (listId: string, body: UpdateListItemUnitDto[]) =>
      callBsl({
        proxy: proxies.inventory,
        verb: Verb.PATCH,
        pathSegments: ['inventory-lists', listId, 'items'],
        payload: { items: body },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const getItemsByInventoryList = useCallback(
    async (
      listId: string,
      showAssigned: boolean,
      options: { page: number; limit: number; filter?: string; sort?: string }
    ) =>
      callBsl<Paginated<ListItem>>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: showAssigned
          ? ['inventory-lists', listId, 'items', 'assigned']
          : ['inventory-lists', listId, 'items'],
        queryParams: getRouteOptions(options),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  /**
   * INVENTORY COUNTING
   */

  // Normalize the API response for inventory location summary:
  // - Maps `rawItem`/`prepItem` to the unified `item` field expected by InventoryEntered
  const normalizeLocationSummaryResponse = (response: LocationSummaryResponse): LocationSummaryResponse => {
    const normalized: LocationSummaryResponse = {
      ...response,
      inventoryLocation: {
        ...response.inventoryLocation,
        inventoryEntered: response.inventoryLocation.inventoryEntered.map((entry) => {
          const itemData = entry.item ?? entry.rawItem ?? entry.prepItem;
          return {
            ...entry,
            item: itemData,
          };
        }),
      },
    };
    return normalized;
  };

  const getInventorySummaryByLocation = useCallback(
    async (
      summaryId: string,
      locationId: string,
      options: { page: number; limit: number; searchValue?: string; includePrepItems?: boolean }
    ) =>
      callBsl<LocationSummaryResponse>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        queryParams: getRouteOptions(options),
        pathSegments: ['inventory-summary', summaryId, 'location', locationId],
        enterpriseUnit: siteId,
      })
        .then(normalizeLocationSummaryResponse)
        .catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  const createInventorySummary = useCallback(
    async (body: CreateInventorySummaryDto, includePrepItems?: boolean) =>
      callBsl<InventorySummaryList>({
        proxy: proxies.inventory,
        verb: Verb.POST,
        payload: body,
        queryParams: getRouteOptions({ includePrepItems }),
        pathSegments: ['inventory-summary'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.POST, siteId, handleBspError, callBsl]
  );

  const getInventorySummary = useCallback(
    async (options: {
      page: number;
      limit: number;
      minValue?: number;
      maxValue?: number;
      minSkippedPercentage?: number;
      maxSkippedPercentage?: number;
      statuses?: MobileInventoryStatus[];
      startDate?: string | null;
      endDate?: string | null;
      sortBy?: string;
      includePrepItems?: boolean;
    }) =>
      callBsl<InventorySummaryResponse>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        queryParams: getRouteOptions(options),
        pathSegments: ['inventory-summary'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getInventorySummaryList = useCallback(
    async (id: string, includePrepItems?: boolean) =>
      callBsl<InventorySummaryList>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: ['inventory-summary', id],
        queryParams: getRouteOptions({ includePrepItems }),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, siteId, handleBspError, callBsl]
  );

  const deleteInventorySummaryList = useCallback(
    async (id: string) =>
      callBsl<InventorySummaryList>({
        proxy: proxies.inventory,
        verb: Verb.DELETE,
        pathSegments: ['inventory-summary', id],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const updateInventorySummaryLocation = useCallback(
    async (
      summaryId: string,
      locationId: string,
      body: UpdateLocationSummary,
      options?: { page?: number; limit?: number; searchValue?: string; includePrepItems?: boolean }
    ) => {
      const queryParams = options ? getRouteOptions(options) : undefined;
      return callBsl({
        proxy: proxies.inventory,
        verb: Verb.PATCH,
        pathSegments: ['inventory-summary', summaryId, 'location', locationId],
        payload: body,
        queryParams,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
    },
    [proxies.inventory, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const updateCountSummary = useCallback(
    async (summaryId: string, body: UpdateCountSummary, includePrepItems?: boolean) =>
      callBsl({
        proxy: proxies.inventory,
        verb: Verb.PATCH,
        pathSegments: ['inventory-summary', summaryId],
        payload: body,
        queryParams: getRouteOptions({ includePrepItems }),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.inventory, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  /** PURCHASE ORDERS */

  const updatePurchaseOrderAcknowledgementStatus = useCallback(
    async (id: string, nepOrganization: string) => {
      // Make proxy url string
      const path = '/authlessbslproxyncrid';
      const proxyUrl = `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}${path}`;

      return axios.post(proxyUrl, {
        data: {
          proxy: 'purchase-orders',
          verb: Verb.PATCH,
          pathSegments: ['purchase-orders', id, 'acknowledge'],
          nepOrganization,
        },
      });
    },
    [Verb.PATCH]
  );

  /**
   * MENU ITEMS
   */

  const getAllSalesItems = useCallback(
    async () =>
      callBsl<GetAllSalesItemsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-item'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getOneSalesItem = useCallback(
    async (itemCode: string) =>
      callBsl<GetOneSalesItemsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-item', itemCode],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getUnmappedSalesItems = useCallback(
    async (keyword?: string, limit?: number, page?: number) => {
      const queryParams: Array<Req.Firebase.SingleQueryParam> = [];
      if (keyword) queryParams.push({ kind: 'single', key: 'keyword', value: keyword });
      if (limit) queryParams.push({ kind: 'single', key: 'limit', value: limit.toString() });
      if (page) queryParams.push({ kind: 'single', key: 'page', value: page.toString() });

      return callBsl<GetUnmappedSalesItemsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-item', 'unmapped'],
        queryParams: queryParams.length > 0 ? queryParams : undefined,
        enterpriseUnit: siteId,
      }).catch(handleBspError);
    },
    [siteId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const createSalesItemMapping = useCallback(
    async (body: SalesItemMappingData) =>
      callBsl<MappingId>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['menu-item'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, siteId, handleBspError, callBsl]
  );

  const updateSalesItemMapping = useCallback(
    async (id: string, body: EntityMappingData) =>
      callBsl<MappingId>({
        proxy: proxies.recipes,
        verb: Verb.PUT,
        pathSegments: ['menu-item', id],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.PUT, siteId, handleBspError, callBsl]
  );

  const deleteSalesItemMapping = useCallback(
    async (id: string) =>
      callBsl<MappingId>({
        proxy: proxies.recipes,
        verb: Verb.DELETE,
        pathSegments: ['menu-item', id],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const getAllRecipesAndRawItems = useCallback(
    async (filterPrepItem?: boolean, find?: string) =>
      callBsl<GetAllSalesItemsMappingsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-item', 'mappings'],
        queryParams: getRouteOptions({ filterPrepItem, find, page: 1, limit: 100 }),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getAllModifierGroups = useCallback(
    async () =>
      callBsl<GetAllModifierGroupsResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-modifier'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getOneModifierGroup = useCallback(
    async (itemCode: string) =>
      callBsl<GetOneModifierResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['menu-modifier', itemCode],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.recipes, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getRecipeItemUnitPrice = useCallback(
    async (recipeItemUnitPriceRequest: RecipeItemUnitPriceRequest) =>
      callBsl<RecipeItemUnitPriceResponse>({
        proxy: proxies.recipes,
        verb: Verb.POST,
        pathSegments: ['recipes', 'recipeItem', 'unitPrice'],
        enterpriseUnit: siteId,
        payload: recipeItemUnitPriceRequest,
      }).catch(handleBspError),
    [proxies.recipes, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getCogsFilters = useCallback(
    async (cogsFiltersRequest: BaseReportDataRequest) =>
      callBsl<CogsFiltersResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['cogs', 'filters'],
        enterpriseUnit: siteId,
        payload: cogsFiltersRequest,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getCogsAvailableDates = useCallback(
    async (enterpriseUnitId: string) =>
      callBsl<string[]>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['cogs', 'available-dates', enterpriseUnitId],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [callBsl, proxies.sales, Verb.GET, siteId, handleBspError]
  );

  const getCogsKPICards = useCallback(
    async (cogsKPICardsRequest: CogsRequest) =>
      callBsl<CogsKPICardsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['cogs', 'kpi-cards'],
        enterpriseUnit: siteId,
        payload: cogsKPICardsRequest,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getCogsDataGrid = useCallback(
    async (body: CogsRequest) =>
      callBsl<CogsDataGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['cogs', 'grid-data'],
        enterpriseUnit: siteId,
        payload: body,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getTop20ItemUsageReportData = useCallback(
    async (body: Top20ItemUsageReportRequest) =>
      callBsl<Top20ItemUsageReportResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['top-bottom-items', 'data'],
        enterpriseUnit: siteId,
        payload: {
          startDate: body.startDate,
          endDate: body.endDate,
          varianceType: body.varianceType,
          siteId: body.siteId,
        },
      }).catch(() => ({
        top20Data: {
          items: [],
          hasVarianceIncreasedTotal: false,
        },
        bottom10Data: {
          items: [],
          hasVarianceIncreasedTotal: false,
        },
      })),
    [proxies.sales, Verb.POST, callBsl, siteId]
  );
  const getPerpetualInventoryKPICards = useCallback(
    async (perpetualInventoryKPICardsRequest: PerpetualInventoryRequest) =>
      callBsl<PerpetualInventoryKPICardsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['perpetual-inventory-on-hand', 'kpi-cards'],
        enterpriseUnit: siteId,
        payload: perpetualInventoryKPICardsRequest,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getPerpetualInventoryDataGrid = useCallback(
    async (body: PerpetualInventoryRequest) =>
      callBsl<PerpetualInventoryDataGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['perpetual-inventory-on-hand', 'grid-data'],
        enterpriseUnit: siteId,
        payload: body,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, siteId, callBsl, handleBspError]
  );

  const getLowStockReportData = useCallback(
    async (body: LowStockReportRequest) =>
      callBsl<LowStockReportResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['low-stock-items', 'data'],
        enterpriseUnit: body.siteId,
        payload: body,
      }).catch(handleBspError),
    [proxies.sales, Verb.POST, callBsl, handleBspError]
  );

  const getRealTimeInventoryOnHand = useCallback(
    async (options: ItemsOnHandQueries) => {
      const queryParams = toQueryParams({
        page: String(options.page ?? 1),
        limit: String(options.limit ?? 50),
        sortBy: options.sortBy ?? 'itemName',
        sortDir: options.sortDir ?? 'asc',
        filterStatus: options.filterStatus ?? 'ALL',
        itemName: options.itemName,
      });
      return callBsl<RawItemsOnHandResponse>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['real-time-inventory', 'raw-items', 'on-hand'],
        queryParams,
        enterpriseUnit: options.siteId,
      }).catch(handleBspError);
    },
    [proxies.recipes, Verb.GET, callBsl, handleBspError]
  );

  /**
   * PURCHASE ORDERS
   */

  const createPurchaseOrder = useCallback(
    async (body: CreatePurchaseOrderDto) =>
      callBsl<PurchaseOrderDetails>({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const deletePurchaseOrder = useCallback(
    async (id: string) =>
      callBsl({
        proxy: proxies.purchaseOrders,
        verb: Verb.DELETE,
        pathSegments: ['purchase-orders', id],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const updatePurchaseOrder = useCallback(
    async (id: string, body: CreatePurchaseOrderDto) =>
      callBsl<PurchaseOrderDetails>({
        proxy: proxies.purchaseOrders,
        verb: Verb.PATCH,
        pathSegments: ['purchase-orders', id],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const getPurchaseOrdersGridData = useCallback(
    async (options: { page: number; limit: number; filters?: string[]; sort?: string }) =>
      callBsl<Paginated<PurchaseOrdersGridRowResponse>>({
        proxy: proxies.purchaseOrders,
        verb: Verb.GET,
        pathSegments: ['purchase-orders'],
        queryParams: getRouteOptions(options),
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getPurchaseOrder = useCallback(
    async (id: string) =>
      callBsl<PurchaseOrderDetails>({
        proxy: proxies.purchaseOrders,
        verb: Verb.GET,
        pathSegments: ['purchase-orders', id],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.GET, siteId, handleBspError, callBsl]
  );

  const createPurchaseOrderItem = useCallback(
    async (id: string, body: CreatePurchaseOrderItemDto) =>
      callBsl<PurchaseOrderItem>({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders', id, 'items'],
        enterpriseUnit: siteId,
        payload: body,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const deletePurchaseOrderItem = useCallback(
    async (purchaseOrderId: string, itemId: string) =>
      callBsl<PurchaseOrderItem>({
        proxy: proxies.purchaseOrders,
        verb: Verb.DELETE,
        pathSegments: ['purchase-orders', purchaseOrderId, 'items', itemId],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const updatePurchaseOrderItem = useCallback(
    async (purchaseOrderId: string, itemId: string, body: UpdatePurchaseOrderItemDto) =>
      callBsl<PurchaseOrderItem>({
        proxy: proxies.purchaseOrders,
        verb: Verb.PATCH,
        pathSegments: ['purchase-orders', purchaseOrderId, 'items', itemId],
        enterpriseUnit: siteId,
        payload: body,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const getPurchaseOrderItems = useCallback(
    async (id: string, options: { page: number; limit: number; filters?: string[]; sort?: string }) =>
      callBsl<Paginated<PurchaseOrderItem>>({
        proxy: proxies.purchaseOrders,
        verb: Verb.GET,
        pathSegments: ['purchase-orders', id, 'items'],
        enterpriseUnit: siteId,
        queryParams: getRouteOptions(options),
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getPurchaseOrderItem = useCallback(
    async (purchaseOrderId: string, purchaseOrderItemId: string) =>
      callBsl<PurchaseOrderItem>({
        proxy: proxies.purchaseOrders,
        verb: Verb.GET,
        pathSegments: ['purchase-orders', purchaseOrderId, 'items', purchaseOrderItemId],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getPurchaseOrderVendorItem = useCallback(
    async (vendorId: string, vendorItemId: string, deliveryDate?: string) =>
      callBsl<VendorItemPurchaseOrderDetails>({
        proxy: proxies.purchaseOrders,
        verb: Verb.GET,
        pathSegments: ['purchase-orders', 'vendor', vendorId, 'items', vendorItemId],
        queryParams: deliveryDate ? [{ kind: 'single', key: 'deliveryDate', value: deliveryDate }] : undefined,
        enterpriseUnit: siteId,
      }).catch((err) => ({
        recentOrders: [],
        currentOnHand: 0,
        itemCode: '',
        openPO: 0,
        requiredQty: 0,
        suggestedQty: 0,
        vendorItem: {} as VendorItemData,
        vendorItemId: '',
        itemName: '',
      })),
    [proxies.purchaseOrders, Verb.GET, siteId, callBsl]
  );

  const bulkDeletePurchaseOrderItems = useCallback(
    async (id: string, itemIds: string[]) =>
      callBsl<unknown>({
        proxy: proxies.purchaseOrders,
        verb: Verb.DELETE,
        pathSegments: ['purchase-orders', id, 'items'],
        enterpriseUnit: siteId,
        payload: { itemIds },
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.DELETE, siteId, handleBspError, callBsl]
  );

  const createSuggestedPurchaseOrder = useCallback(
    async (payload: CreateSuggestedPurchaseOrderDto) =>
      callBsl<CreateSuggestedPurchaseOrderResponseDto>({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['suggested-ordering', 'purchase-orders'],
        enterpriseUnit: siteId,
        payload,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const sendPurchaseOrderEmail = useCallback(
    async (id: string, userEmail: string) =>
      callBsl({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders', id, 'pdf'],
        payload: { userEmail },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const sendPurchaseOrderFtp = useCallback(
    async (id: string) =>
      callBsl({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders', id, 'send-to-vendor'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const completePurchaseOrder = useCallback(
    async (id: string) =>
      callBsl({
        proxy: proxies.purchaseOrders,
        verb: Verb.PATCH,
        pathSegments: ['purchase-orders', id, 'complete'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.PATCH, siteId, handleBspError, callBsl]
  );

  const updateVendorSftpPassword = useCallback(
    async (vendorId: string, password: string) =>
      callBsl<unknown>({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders', 'vendor', vendorId, 'sftp-password'],
        payload: { password },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const generatePoNumber = useCallback(
    async (vendorId: string) =>
      callBsl<GeneratePoNumberResponseDto>({
        proxy: proxies.purchaseOrders,
        verb: Verb.POST,
        pathSegments: ['purchase-orders', 'generate-po-number'],
        payload: { vendorId },
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.purchaseOrders, Verb.POST, siteId, handleBspError, callBsl]
  );

  const getUsagePer = useCallback(
    async (rawItemId: string): Promise<number> =>
      callBsl<number>({
        proxy: proxies.recipes,
        verb: Verb.GET,
        pathSegments: ['rawItems', 'get-usage-per'],
        queryParams: [
          { kind: 'single', key: 'siteId', value: siteId },
          { kind: 'single', key: 'rawItemId', value: rawItemId },
        ],
      }).catch(handleBspError),
    [callBsl, proxies.recipes, Verb.GET, siteId, handleBspError]
  );

  const getDefaultUsagePerFactor = async (): Promise<number> => mockDefaultUsagePerFactor;

  const updateUsagePer = useCallback(
    async (rawItemId: string, body: UpdateUsagePerData): Promise<number> =>
      callBsl<number>({
        proxy: proxies.recipes,
        verb: Verb.PUT,
        pathSegments: ['rawItems', 'update-usage-per-factor'],
        enterpriseUnit: bslSiteId,
        payload: { rawItemId, ...body },
      }).catch(handleBspError),
    [proxies.recipes, Verb.PUT, handleBspError, callBsl, bslSiteId]
  );

  const upsertVendorOrderDeliveryPairs = useCallback(
    async (id: string, body: UpsertOrderDeliveryPairs[]): Promise<string> =>
      callBsl<string>({
        proxy: proxies.vendors,
        verb: Verb.PUT,
        pathSegments: ['vendors', 'order-pairs', id],
        payload: body,
      }).catch(handleBspError),
    [proxies.vendors, Verb.PUT, handleBspError, callBsl]
  );

  /**
   * VALUATION SETTINGS
   */
  const getValuationSettings = useCallback(
    async (): Promise<ValuationSettings> =>
      callBsl<ValuationSettings>({
        proxy: proxies.inventory,
        verb: Verb.GET,
        pathSegments: ['inventory-valuation-config'],
      }).catch(handleBspError),
    [proxies.inventory, Verb.GET, handleBspError, callBsl]
  );

  const updateValuationSettings = useCallback(
    async (valuationSettings: ValuationSettings): Promise<ValuationSettings> =>
      callBsl<ValuationSettings>({
        proxy: proxies.inventory,
        verb: Verb.PUT,
        pathSegments: ['inventory-valuation-config'],
        payload: valuationSettings,
      }).catch(handleBspError),
    [proxies.inventory, Verb.PUT, handleBspError, callBsl]
  );

  /*
   * INVOICE TEMPLATES
   */
  const createInvoiceTemplate = useCallback(
    async (vendorId: string, body: CreateInvoiceTemplateDto) =>
      callBsl<InvoiceTemplate>({
        proxy: proxies.vendors,
        verb: Verb.POST,
        pathSegments: ['vendor', vendorId, 'invoice-templates'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.POST, siteId, handleBspError, callBsl]
  );

  const getInvoiceTemplates = useCallback(
    async (vendorIds: string[], templateName: string, page: number, limit: number) =>
      callBsl<Paginated<InvoiceTemplate>>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendor', 'invoice-templates'],
        queryParams: [
          { kind: 'multi', key: 'vendorIds', value: vendorIds },
          { kind: 'single', key: 'templateName', value: templateName },
          { kind: 'single', key: 'page', value: page.toString() },
          { kind: 'single', key: 'limit', value: limit.toString() },
        ],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getInvoiceTemplate = useCallback(
    async (vendorId: string, id: string) =>
      callBsl<InvoiceTemplate>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendor', vendorId, 'invoice-templates', id],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  const getVendorTemplateFilterOptions = useCallback(
    async () =>
      callBsl<VendorTemplateFilterOptions[]>({
        proxy: proxies.vendors,
        verb: Verb.GET,
        pathSegments: ['vendor', 'invoice-templates', 'filter-options'],
        enterpriseUnit: siteId,
      }).catch(handleBspError),
    [proxies.vendors, Verb.GET, siteId, handleBspError, callBsl]
  );

  return {
    createRecipe,
    cloneRecipe,
    createRecipeItem,
    updateRecipe,
    deleteRecipe,
    updateRecipeItem,
    bulkUpdateRecipeItems,
    deleteRecipeItem,
    getRecipes,
    findVendorsByName,
    getVendorsPaginatedData,
    getVendorData,
    createNonDraftVendor,
    createDraftVendor,
    updateVendor,
    getInvoicesPaginatedData,
    getInvoicesByVendorData,
    getInvoiceData,
    createInvoice,
    updateInvoice,
    setVendorActive,
    deleteInvoice,
    enableInvoiceEdit,
    updateInvoiceStatus,
    getInvoiceItemData,
    getInvoiceItemsData,
    createInvoiceItemData,
    updateInvoiceItemData,
    deleteInvoiceItem,
    getVendorItemsData,
    getVendorItemData,
    createDraftVendorItemData,
    createNonDraftVendorItemData,
    updateVendorItemData,
    updateNonDraftVendorItemData,
    bulkUpdateVendorItemMapping,
    updateVendorItemActiveStatus,
    getServiceItemsData,
    getContainerData,
    getStartingUnitsData,
    getUnitsData,
    getAllUnitsData,
    getCustomUnitsData,
    getCustomUnit,
    getCombinedUnitsData,
    getIsInUseData,
    deleteCustomUnitById,
    getReceivingUnitData,
    getAllReceivingUnitData,
    getAllReceivingUnitDataBySite,
    createCustomUnit,
    updateCustomUnitById,
    isDuplicateError,
    getRawItems,
    getRawItem,
    calculateSiteUsagePer,
    getUsagePerLastCalcDate,
    validateRawItemCustomUnits,
    updateRawItem,
    updateRawItemActiveStatus,
    updateReportingUnit,
    updateRecipeUnit,
    getReportingUnit,
    getRecipeUnit,
    getUsagePer,
    getDefaultUsagePerFactor,
    updateUsagePer,
    createRawItem,
    createRawItemNoDeactivation,
    deleteRawItem,
    getCostCalcMethodOptions,
    mapCustomUnit,
    mapIndustryUnit,
    removeInventoryUnitMapping,
    getGlCategories,
    getRawItemMappedVendorItems,
    getRawItemMappedVendorItemUnits,
    getRawItemMappedVendorItemUnitClasses,
    getInventoryLocations,
    bulkUpdateLocations,
    getInventoryLists,
    getInventorySummary,
    getInventorySummaryList,
    deleteInventorySummaryList,
    updateInventoryList,
    getItemsByInventoryList,
    getInventorySummaryByLocation,
    updateCountSummary,
    createInventorySummary,
    updateInventorySummaryLocation,
    getRawItemsByLocation,
    updateLocationItemUnit,
    deleteLocationItem,
    addOcrMetadata,
    updateOcrMetadata,
    getVendorSummaryReportInvoiceCountCardData,
    getVendorSummaryReportInvoiceAmountCardData,
    getInvoiceTotalAmountByVendorData,
    getVendorSummaryReportGridData,
    getVendorDetailReportCardsData,
    getVendorDetailReportGridData,
    getGLAccountReportGridData,
    getGLAccountReportKPINumbersData,
    getGLAccountReportKPIChartData,
    getGLAccountDetailReportCardsData,
    getGLAccountDetailReportGridData,
    getRecipesPaginatedData,
    getRecipesFilterOptionsData,
    getRecipesFilterOptionsByFieldData,
    getRecipeItemsPaginatedData,
    getRecipeData,
    getUnassignedPrepAndRawItems,
    getAllSalesItems,
    getOneSalesItem,
    getUnmappedSalesItems,
    createSalesItemMapping,
    updateSalesItemMapping,
    deleteSalesItemMapping,
    getAllRecipesAndRawItems,
    getAllModifierGroups,
    getOneModifierGroup,
    getRecipeItemUnitPrice,
    getCogsKPICards,
    getCogsFilters,
    getCogsAvailableDates,
    getCogsDataGrid,
    getTop20ItemUsageReportData,
    getPerpetualInventoryKPICards,
    getPerpetualInventoryDataGrid,
    getLowStockReportData,
    getRealTimeInventoryOnHand,
    createPurchaseOrder,
    updatePurchaseOrder,
    deletePurchaseOrder,
    getPurchaseOrdersGridData,
    getPurchaseOrder,
    createPurchaseOrderItem,
    deletePurchaseOrderItem,
    updatePurchaseOrderItem,
    getPurchaseOrderItem,
    getPurchaseOrderVendorItem,
    getPurchaseOrderItems,
    bulkDeletePurchaseOrderItems,
    createSuggestedPurchaseOrder,
    sendPurchaseOrderEmail,
    sendPurchaseOrderFtp,
    completePurchaseOrder,
    generatePoNumber,
    updateVendorSftpPassword,
    upsertVendorOrderDeliveryPairs,
    updatePurchaseOrderAcknowledgementStatus,
    getValuationSettings,
    updateValuationSettings,
    createInvoiceTemplate,
    getInvoiceTemplates,
    getInvoiceTemplate,
    getVendorTemplateFilterOptions,
  };
};

export default useInventoryRequests;
